<template>
    <div class="print-toolbar-footer">
        <div class="print-toolbar-container">
            <div class="print-selected-detail">
                <template v-if="isPrinting">
                    共選擇
                    <span class="detail-highlight">{{ totalNum }}</span>
                    項商品
                </template>
                <template v-else-if="isDownloading">
                    共
                    <span class="detail-highlight">{{ pageNum }}</span>
                    頁
                </template>
            </div>

            <div class="print-btn-list">
                <template v-if="isPrinting">
                    <div
                        ref="printPopupRef"
                        style="
                            position: relative;
                            display: flex;
                            justify-content: center;
                            cursor: pointer;
                        "
                    >
                        <svg-icon
                            icon-name="question-solid"
                            color="#8ad1ff"
                        ></svg-icon>
                        <gh-tooltip
                            ref="printTooltipRef"
                            :position="TooltipPosition.Top"
                            style="max-width: 290px; font-size: 16px"
                        >
                            <ul class="numerical-list">
                                <li v-for="text in tooltipText" :key="text">
                                    {{ text }}
                                </li>
                            </ul>
                        </gh-tooltip>
                    </div>

                    <button
                        type="button"
                        class="btn btn-white-086efb print-close"
                        @click="stopPrinting"
                    >
                        關閉
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="exportReport"
                    >
                        輸出商品清單
                    </button>
                </template>

                <template v-else-if="isDownloading">
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="print"
                    >
                        列印 PDF
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import SvgIcon from '@/components/base/SvgIcon.vue';
    import GhTooltip from '@/components/base/GhTooltip.vue';
    import { TooltipPosition } from '@/types/components/base-components.types';
    import type { PrintProductItem } from '@/types/utils/print.utils.types';

    interface TooltipComponentInstance {
        $el?: HTMLElement;
    }

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            title: string;
            items: PrintProductItem[];
            isPrinting?: boolean;
            isDownloading?: boolean;
            pageNum?: number;
        }>(),
        {
            isPrinting: false,
            isDownloading: false,
            pageNum: 0,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'update-printing'): void;
        (event: 'export'): void;
        (event: 'export-error', message: string): void;
    }>();

    // ----- state -----
    const printPopupRef = ref<HTMLElement | null>(null);
    const printTooltipRef = ref<TooltipComponentInstance | null>(null);
    const tooltipTimeoutIds = ref<number[]>([]);
    const tooltipEnterHandlerRef = ref<((event: Event) => void) | null>(null);
    const tooltipLeaveHandlerRef = ref<((event: Event) => void) | null>(null);
    const tooltipText = ref<string[]>([
        '請選擇要列印的商品。',
        '可以個別勾選，或使用「全選」、「取消選取」快速變更商品。',
        '核對商品清單內容後，點擊「輸出商品清單」輸出 PDF。',
        '若無需列印，可點擊「關閉」結束操作。',
    ]);

    // ----- computed -----
    const totalNum = computed<number>(() => {
        return props.items.length;
    });

    // ----- methods -----
    const clearTooltipTimeouts = (): void => {
        tooltipTimeoutIds.value.forEach((id) => {
            window.clearTimeout(id);
        });
        tooltipTimeoutIds.value = [];
    };

    const toggleTooltip = (isDisplay: boolean): void => {
        const tooltipElement = printTooltipRef.value?.$el;
        if (!tooltipElement) {
            return;
        }

        if (isDisplay) {
            clearTooltipTimeouts();
            tooltipElement.classList.add('gh-tooltip-active');
            return;
        }

        tooltipElement.classList.remove('gh-tooltip-active');
    };

    // 結束列印選取模式
    const stopPrinting = (): void => {
        emits('update-printing');
    };

    // 輸出報表後切換到列印頁面
    const exportReport = (): void => {
        if (props.isPrinting && props.items.length > 0) {
            emits('export');
            sessionStorage.setItem('printTitle', props.title);
            sessionStorage.setItem('printData', JSON.stringify(props.items));
            stopPrinting();
            window.open('/Print/PrintProducts');
            return;
        }

        emits('export-error', '您尚未選取任何商品，請勾選商品後再操作！');
    };

    // 等待圖片加載完成
    const waitImagesReady = async (root = document) => {
        const images = Array.from(root.querySelectorAll('img'));
        images.forEach((img) => {
            img.loading = 'eager';
        });

        await Promise.all(
            images.map((img) => {
                if (img.complete) return Promise.resolve();
                return new Promise((resolve) => {
                    img.addEventListener('load', resolve, { once: true });
                    img.addEventListener('error', resolve, { once: true });
                });
            })
        );

        await Promise.all(
            images.map((img) => img.decode?.().catch(() => undefined))
        );
    };

    // 呼叫瀏覽器列印視窗
    const print = async (): Promise<void> => {
        await waitImagesReady();
        window.print();
    };

    const bindTooltipHoverEvents = (): void => {
        const popupElement = printPopupRef.value;
        if (!popupElement) {
            return;
        }

        tooltipEnterHandlerRef.value = () => {
            toggleTooltip(true);
        };
        tooltipLeaveHandlerRef.value = () => {
            toggleTooltip(false);
        };

        popupElement.addEventListener(
            'mouseenter',
            tooltipEnterHandlerRef.value
        );
        popupElement.addEventListener(
            'mouseleave',
            tooltipLeaveHandlerRef.value
        );

        toggleTooltip(true);
        const hideTimer = window.setTimeout(() => {
            toggleTooltip(false);
        }, 5000);
        tooltipTimeoutIds.value.push(hideTimer);
    };

    const unbindTooltipHoverEvents = (): void => {
        const popupElement = printPopupRef.value;
        if (!popupElement) {
            return;
        }

        if (tooltipEnterHandlerRef.value) {
            popupElement.removeEventListener(
                'mouseenter',
                tooltipEnterHandlerRef.value
            );
            tooltipEnterHandlerRef.value = null;
        }

        if (tooltipLeaveHandlerRef.value) {
            popupElement.removeEventListener(
                'mouseleave',
                tooltipLeaveHandlerRef.value
            );
            tooltipLeaveHandlerRef.value = null;
        }
    };

    onMounted(() => {
        bindTooltipHoverEvents();
    });

    onBeforeUnmount(() => {
        unbindTooltipHoverEvents();
        clearTooltipTimeouts();
    });
</script>
